'use client';

import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Video,
  Link,
  X,
  AlertCircle,
  Play,
  Code,
  VolumeX,
  Repeat,
  EyeOff,
  Info,
  Move,
  Check,
  Plus,
  Minus,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

import { usePageBuilderContext } from '../page-builder-context';
import { EmbedFrame } from '../embed-frame';
import {
  parseVideoUrl,
  getVideoEmbedUrl,
  parseEmbedInput,
  normalizeEmbedSrc,
  detectEmbedProvider,
  isConfigurableEmbedProvider,
  getEmbedSrcOptions,
  setEmbedSrcOptions,
  DEFAULT_VIDEO_ASPECT_RATIO,
  EMBED_SCALE_STEP,
  EMBED_SCALE_MIN,
  EMBED_SCALE_MAX,
  EMBED_OFFSET_STEP,
  embedMaxOffset,
} from '@/lib/types/page-builder';
import type {
  VideoBlock,
  IframeEmbedOptions,
  EmbedProvider,
} from '@/lib/types/page-builder';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

// ============================================================================
// TYPES
// ============================================================================

interface VideoBlockEditorProps {
  block: VideoBlock;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function VideoBlockEditor({ block }: VideoBlockEditorProps) {
  const { updateBlock } = usePageBuilderContext();
  const [urlInput, setUrlInput] = useState(block.content.url);
  const [iframeInput, setIframeInput] = useState(block.content.embedSrc || '');
  const [inputMode, setInputMode] = useState<'url' | 'iframe'>(block.content.inputMode || 'iframe');
  const [isEditing, setIsEditing] = useState(!block.content.embedId && !block.content.embedSrc);
  const [error, setError] = useState<string | null>(null);

  const hasVideo = !!block.content.embedId || !!block.content.embedSrc;

  const handleUrlChange = useCallback(
    (url: string) => {
      setUrlInput(url);
      setError(null);

      if (!url.trim()) {
        updateBlock(block.id, {
          content: { ...block.content, url: '', embedId: '', provider: 'youtube', embedSrc: undefined, inputMode: 'url', aspectRatio: undefined },
        });
        return;
      }

      const parsed = parseVideoUrl(url);
      if (parsed) {
        // For providers with configurable player options (Vimeo),
        // synthesize the player src so the options toolbar is available
        // regardless of whether the user pasted a URL or an iframe.
        const embedSrc =
          parsed.provider === 'vimeo'
            ? normalizeEmbedSrc(getVideoEmbedUrl('vimeo', parsed.embedId))
            : undefined;

        updateBlock(block.id, {
          content: {
            ...block.content,
            url,
            embedId: parsed.embedId,
            provider: parsed.provider,
            embedSrc,
            inputMode: 'url',
            // Ratio is unknown from a bare URL; fall back to 16:9.
            aspectRatio: undefined,
          },
        });
        setIsEditing(false);
      } else {
        setError('Неверная ссылка. Поддерживаются YouTube и Vimeo');
      }
    },
    [block.id, block.content, updateBlock]
  );

  const handleEmbedChange = useCallback(
    (input: string) => {
      setIframeInput(input);
      setError(null);

      if (!input.trim()) {
        updateBlock(block.id, {
          content: { ...block.content, embedSrc: undefined, url: '', embedId: '', inputMode: 'iframe', aspectRatio: undefined },
        });
        return;
      }

      const parsed = parseEmbedInput(input);
      if (parsed) {
        updateBlock(block.id, {
          content: {
            ...block.content,
            embedSrc: parsed.src,
            url: '',
            embedId: '',
            inputMode: 'iframe',
            // A re-parse that can't see the ratio (e.g. blurring the stored
            // src, which carries none) must never erase a known one.
            aspectRatio: parsed.aspectRatio ?? block.content.aspectRatio ?? DEFAULT_VIDEO_ASPECT_RATIO,
          },
        });
        setIframeInput(parsed.src);
        setIsEditing(false);
      } else {
        setError('Неверный код. Вставьте тег <iframe>...</iframe> или ссылку на плеер');
      }
    },
    [block.id, block.content, updateBlock]
  );

  const embedSrc = block.content.embedSrc || null;

  const embedProvider: EmbedProvider = useMemo(
    () => detectEmbedProvider(embedSrc),
    [embedSrc]
  );

  const embedOptions = useMemo<IframeEmbedOptions | null>(
    () => (embedSrc ? getEmbedSrcOptions(embedSrc) : null),
    [embedSrc]
  );

  const handleToggleOption = useCallback(
    (key: keyof IframeEmbedOptions) => {
      if (!embedSrc || !embedOptions) return;
      const next: IframeEmbedOptions = { ...embedOptions, [key]: !embedOptions[key] };
      // Autoplay and muted are coupled: browsers only allow autoplay
      // without a user gesture when the player is muted.
      if (key === 'autoplay' && next.autoplay && !next.muted) {
        next.muted = true;
      }
      if (key === 'muted' && !next.muted && next.autoplay) {
        next.autoplay = false;
      }
      const updatedSrc = setEmbedSrcOptions(embedSrc, next);
      updateBlock(block.id, {
        content: { ...block.content, embedSrc: updatedSrc },
      });
      setIframeInput(updatedSrc);
    },
    [embedSrc, embedOptions, block.id, block.content, updateBlock]
  );

  const [moveMode, setMoveMode] = useState(false);

  const handleResize = useCallback(
    (delta: number) => {
      const current = block.content.scalePercent ?? EMBED_SCALE_MIN;
      const scalePercent = Math.min(EMBED_SCALE_MAX, Math.max(EMBED_SCALE_MIN, current + delta));
      // Shrinking tightens the pan bounds — re-clamp so edges never show.
      const maxOffset = embedMaxOffset(scalePercent);
      const clamp = (v: number) => Math.min(maxOffset, Math.max(-maxOffset, v));
      updateBlock(block.id, {
        content: {
          ...block.content,
          scalePercent,
          offsetX: clamp(block.content.offsetX ?? 0),
          offsetY: clamp(block.content.offsetY ?? 0),
        },
      });
    },
    [block.id, block.content, updateBlock]
  );

  const handleNudge = useCallback(
    (dx: number, dy: number) => {
      const maxOffset = embedMaxOffset(block.content.scalePercent ?? EMBED_SCALE_MIN);
      const clamp = (v: number) => Math.min(maxOffset, Math.max(-maxOffset, v));
      updateBlock(block.id, {
        content: {
          ...block.content,
          offsetX: clamp((block.content.offsetX ?? 0) + dx * EMBED_OFFSET_STEP),
          offsetY: clamp((block.content.offsetY ?? 0) + dy * EMBED_OFFSET_STEP),
        },
      });
    },
    [block.id, block.content, updateBlock]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && inputMode === 'url') {
      e.preventDefault();
      handleUrlChange(urlInput);
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setUrlInput(block.content.url);
      setIframeInput(block.content.embedSrc || '');
    }
  };

  const handleRemove = () => {
    updateBlock(block.id, {
      content: { url: '', embedId: '', provider: 'youtube', caption: '', embedSrc: undefined, inputMode: 'iframe', aspectRatio: undefined },
    });
    setUrlInput('');
    setIframeInput('');
    setInputMode('iframe');
    setIsEditing(true);
  };

  // URL mode (YouTube) renders from the derived embed URL
  const embedUrl = block.content.embedId
    ? getVideoEmbedUrl(block.content.provider, block.content.embedId)
    : null;

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {hasVideo && !isEditing ? (
          // Video embed
          <motion.div
            key="video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative group"
          >
            <EmbedFrame
              src={embedSrc || embedUrl || ''}
              aspectRatio={block.content.aspectRatio}
              scalePercent={block.content.scalePercent}
              offsetX={block.content.offsetX}
              offsetY={block.content.offsetY}
              title="Video embed"
            >
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none" />

              {/* Controls */}
              <div
                className={cn(
                  'absolute top-3 right-3 flex gap-2 transition-opacity',
                  moveMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
              >
                {moveMode ? (
                  <>
                    {/* Keyboard-style arrow cluster: ↑ on top, ← ↓ → below */}
                    <div className="flex flex-col items-center gap-1">
                      <OverlayIconButton title="Сдвинуть вверх" onClick={() => handleNudge(0, -1)}>
                        <ArrowUp className="h-4 w-4" />
                      </OverlayIconButton>
                      <div className="flex gap-1">
                        <OverlayIconButton title="Сдвинуть влево" onClick={() => handleNudge(-1, 0)}>
                          <ArrowLeft className="h-4 w-4" />
                        </OverlayIconButton>
                        <OverlayIconButton title="Сдвинуть вниз" onClick={() => handleNudge(0, 1)}>
                          <ArrowDown className="h-4 w-4" />
                        </OverlayIconButton>
                        <OverlayIconButton title="Сдвинуть вправо" onClick={() => handleNudge(1, 0)}>
                          <ArrowRight className="h-4 w-4" />
                        </OverlayIconButton>
                      </div>
                    </div>
                    <OverlayIconButton title="Готово" onClick={() => setMoveMode(false)}>
                      <Check className="h-4 w-4" />
                    </OverlayIconButton>
                  </>
                ) : (
                  <>
                    <OverlayIconButton title="Переместить видео в рамке" onClick={() => setMoveMode(true)}>
                      <Move className="h-4 w-4" />
                    </OverlayIconButton>
                    <OverlayIconButton title="Уменьшить (до 100%)" onClick={() => handleResize(-EMBED_SCALE_STEP)}>
                      <Minus className="h-4 w-4" />
                    </OverlayIconButton>
                    <OverlayIconButton title="Увеличить видео в рамке" onClick={() => handleResize(EMBED_SCALE_STEP)}>
                      <Plus className="h-4 w-4" />
                    </OverlayIconButton>
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg backdrop-blur-sm"
                    >
                      Изменить
                    </button>
                    <button
                      type="button"
                      onClick={handleRemove}
                      className="p-1.5 bg-white/20 hover:bg-destructive text-white rounded-lg backdrop-blur-sm"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </EmbedFrame>

            {embedSrc && embedOptions && isConfigurableEmbedProvider(embedProvider) && (
              <div className="mt-3 pb-3">
                <div className="flex flex-wrap items-center gap-1">
                  <EmbedOptionsBar
                    provider={embedProvider}
                    options={embedOptions}
                    onToggle={handleToggleOption}
                  />
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          // Input form
          <motion.div
            key="input"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={cn(
              'p-8',
              'transition-colors duration-200',
              error && 'bg-destructive/5'
            )}
          >
            <div className="max-w-md mx-auto space-y-4">
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mx-auto">
                <Video className="h-7 w-7 text-muted-foreground" />
              </div>

              {/* Title */}
              <div className="text-center">
                <h3 className="text-base font-medium text-foreground">
                  Добавить видео
                </h3>
                <p className="text-sm text-foreground-tertiary mt-1">
                  Вставьте код iframe или ссылку на видео
                </p>
              </div>

              {/* Mode Tabs */}
              <Tabs value={inputMode} onValueChange={(v) => setInputMode(v as 'url' | 'iframe')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="iframe" className="flex items-center gap-1.5">
                    <Code className="h-3.5 w-3.5" />
                    <span>Iframe</span>
                    <span className="text-[10px] text-success font-normal ml-0.5">лучше</span>
                  </TabsTrigger>
                  <TabsTrigger value="url" className="flex items-center gap-1.5">
                    <Link className="h-3.5 w-3.5" />
                    <span>Ссылка</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Iframe Input */}
              {inputMode === 'iframe' && (
                <div className="space-y-3">
                  <textarea
                    value={iframeInput}
                    onChange={(e) => setIframeInput(e.target.value)}
                    onBlur={() => handleEmbedChange(iframeInput)}
                    placeholder='<iframe src="..." width="560" height="315" ...></iframe>'
                    rows={4}
                    className={cn(
                      'w-full px-4 py-2.5 rounded-lg resize-none',
                      'bg-muted/50 border border-border',
                      'text-sm placeholder:text-foreground-tertiary font-mono',
                      'focus:outline-none focus:ring-2 focus:ring-border focus:border-border',
                      error && 'border-destructive focus:ring-destructive/20'
                    )}
                    autoFocus
                  />

                  {/* Recommendation */}
                  <div className="rounded-lg bg-muted/50 px-3.5 py-3 space-y-1.5">
                    <p className="text-xs text-foreground-secondary">
                      Рекомендуем{' '}
                      <a href="https://kinescope.ru" target="_blank" rel="noopener noreferrer" className="text-foreground font-medium underline underline-offset-2">Kinescope</a>{' '}
                      или{' '}
                      <a href="https://vimeo.com" target="_blank" rel="noopener noreferrer" className="text-foreground font-medium underline underline-offset-2">Vimeo</a>{' '}
                      — они поддерживают автоплей без звука, и видео начнёт воспроизводиться сразу при открытии страницы
                    </p>
                    <p className="text-[11px] text-foreground-tertiary">
                      Также подойдут{' '}
                      <a href="https://vk.com/video" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">VK Видео</a>,{' '}
                      <a href="https://rutube.ru" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Rutube</a>{' '}
                      и другие платформы с поддержкой iframe
                    </p>
                  </div>
                </div>
              )}

              {/* URL Input */}
              {inputMode === 'url' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-tertiary" />
                    <input
                      type="url"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      onBlur={() => handleUrlChange(urlInput)}
                      onKeyDown={handleKeyDown}
                      placeholder="https://youtube.com/watch?v=..."
                      className={cn(
                        'w-full pl-10 pr-4 py-2.5 rounded-lg',
                        'bg-muted/50 border border-border',
                        'text-sm placeholder:text-foreground-tertiary',
                        'focus:outline-none focus:ring-2 focus:ring-border focus:border-border',
                        error && 'border-destructive focus:ring-destructive/20'
                      )}
                      autoFocus
                    />
                  </div>

                  {/* Supported platforms */}
                  <div className="flex items-center justify-center gap-4 text-xs text-foreground-tertiary">
                    <span className="flex items-center gap-1">
                      <Play className="h-3 w-3" />
                      YouTube
                    </span>
                    <span className="flex items-center gap-1">
                      <Play className="h-3 w-3" />
                      Vimeo
                    </span>
                  </div>
                </div>
              )}

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-2 text-sm text-destructive"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Cancel button if editing existing */}
              {hasVideo && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setUrlInput(block.content.url);
                    setIframeInput(block.content.embedSrc || '');
                    setError(null);
                  }}
                  className="w-full text-sm text-foreground-tertiary hover:text-foreground"
                >
                  Отмена
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// EMBED OPTIONS BAR
// ============================================================================

interface ProviderMeta {
  label: string;
  dotClass: string;
  activeClass: string;
  ringClass: string;
}

// Active toggles use the Kinescope indigo, not the site primary (rose):
// rose reads as an error/danger state, so users can't tell on from off.
const EMBED_TOGGLE_ACTIVE_CLASS = 'bg-[#4F46E5] text-white hover:bg-[#4338CA]';
const EMBED_TOGGLE_RING_CLASS = 'focus-visible:ring-[#4F46E5]/50';

const PROVIDER_META: Record<'kinescope' | 'vimeo', ProviderMeta> = {
  kinescope: {
    label: 'Kinescope',
    dotClass: 'bg-[#4F46E5]',
    activeClass: EMBED_TOGGLE_ACTIVE_CLASS,
    ringClass: EMBED_TOGGLE_RING_CLASS,
  },
  vimeo: {
    label: 'Vimeo',
    dotClass: 'bg-[#1AB7EA]',
    activeClass: EMBED_TOGGLE_ACTIVE_CLASS,
    ringClass: EMBED_TOGGLE_RING_CLASS,
  },
};

interface EmbedOptionToggle {
  key: keyof IframeEmbedOptions;
  label: string;
  title: string;
  icon: React.ReactNode;
  isActive: (options: IframeEmbedOptions) => boolean;
}

const EMBED_TOGGLES: EmbedOptionToggle[] = [
  {
    key: 'autoplay',
    label: 'Автоплей',
    title: 'Автоматическое воспроизведение',
    icon: <Play className="h-3.5 w-3.5" />,
    isActive: (o) => o.autoplay,
  },
  {
    key: 'muted',
    label: 'Без звука',
    title: 'Воспроизводить без звука',
    icon: <VolumeX className="h-3.5 w-3.5" />,
    isActive: (o) => o.muted,
  },
  {
    key: 'loop',
    label: 'Повтор',
    title: 'Зациклить воспроизведение',
    icon: <Repeat className="h-3.5 w-3.5" />,
    isActive: (o) => o.loop,
  },
  {
    key: 'controls',
    label: 'Без панели',
    title: 'Скрыть панель управления',
    icon: <EyeOff className="h-3.5 w-3.5" />,
    // "Без панели" is ON when controls are hidden
    isActive: (o) => !o.controls,
  },
];

interface EmbedOptionsBarProps {
  provider: 'kinescope' | 'vimeo';
  options: IframeEmbedOptions;
  onToggle: (key: keyof IframeEmbedOptions) => void;
}

function EmbedOptionsBar({ provider, options, onToggle }: EmbedOptionsBarProps) {
  const meta = PROVIDER_META[provider];

  return (
    <>
      {/* Provider badge */}
      <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5">
        <span
          className={cn('inline-block h-1.5 w-1.5 rounded-full', meta.dotClass)}
          aria-hidden
        />
        <span className="text-xs font-semibold text-foreground">{meta.label}</span>
      </div>

      {/* Divider */}
      <span className="mx-0.5 h-5 w-px bg-border/80" aria-hidden />

      {/* Toggles */}
      {EMBED_TOGGLES.map((toggle) => {
        const isActive = toggle.isActive(options);
        return (
          <button
            key={toggle.key}
            type="button"
            onClick={() => onToggle(toggle.key)}
            title={toggle.title}
            aria-pressed={isActive}
            aria-label={toggle.label}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium',
              'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              meta.ringClass,
              isActive
                ? meta.activeClass
                : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
            )}
          >
            {toggle.icon}
            <span>{toggle.label}</span>
          </button>
        );
      })}

      {provider === 'kinescope' && (
        <>
          <span className="mx-0.5 h-5 w-px bg-border/80" aria-hidden />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-foreground-tertiary hover:bg-muted hover:text-foreground-secondary transition-colors"
              >
                <Info className="h-3.5 w-3.5" />
                <span>Инфо</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px]">
              Скругление углов видео можно изменить в настройках плеера на сайте Kinescope
            </TooltipContent>
          </Tooltip>
        </>
      )}
    </>
  );
}

// ============================================================================
// OVERLAY ICON BUTTON
// ============================================================================

interface OverlayIconButtonProps {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}

function OverlayIconButton({ title, onClick, children }: OverlayIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="p-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg backdrop-blur-sm"
    >
      {children}
    </button>
  );
}
